<template>
<div>
    <div v-if="pageData.games || pageData.englishGames && pageData.content" class="category flex-column padded">
        <div v-if="pageData.content" class="flex-column category-info">
            <div class="flex-row">
                <i v-if="pageData.content?.icon" :class="pageData.content?.icon" class="category-icon" />
                <h1 v-if="pageData.content.name">{{ pageData.content.name }}</h1>
            </div>
            <h2 v-if="pageData.content.description">{{ pageData.content.description }}</h2>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-if="pageData.content?.blurb" id="blurb" class="blurb" v-html="pageData.content?.blurb" />
        </div>

        <div v-if="pageData.games && pageData.games.length > 0" class="grid-container">
            <h4 v-if="$i18n.locale !== 'en'" class="locale-heading section-heading">
                {{ $t('GAMES.FILTERED_LANG', { selectedLanguage: currentLanguage }) }}
            </h4>
            <div class="category-grid" :class="{centered: pageData.games.length < 4}">
                <div
                    v-for="(game, index) in pageData.games" :key="`${game.title}-${index}`"
                >
                    <GameTile
                        :game="game"
                        class="game-tile" :image-width-on-request="reqImageWidth"
                    />
                </div>
            </div>
        </div>

        <div v-if="pageData.englishGames && pageData.englishGames.length > 0" class="grid-container">
            <h4 v-if="$i18n.locale !== 'en'" class="locale-heading section-heading">
                {{ $t('GAMES.IN_ENGLISH', { selectedLanguage: currentLanguage }) }}
            </h4>
            <div class="category-grid" :class="{centered: pageData.englishGames.length < 4}">
                <div
                    v-for="(enGame, index) in pageData.englishGames" :key="`${enGame.title}-${index}`"
                >
                    <GameTile
                        :game="enGame"
                        class="game-tile" :image-width-on-request="reqImageWidth"
                    />
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script setup lang="ts">
import type { JBGWeb } from '$types/JBGWeb'
import GameTile from '$components/GameTile.vue'

// eslint-disable-next-line complexity
const mapStringToNumber = (numberString: string | null) => {
    switch (numberString) {
    case 'one':
    case 'single':
        return 1
    case 'two': return 2
    case 'three': return 3
    case 'four': return 4
    case 'five': return 5
    case 'six': return 6
    case 'seven': return 7
    case 'eight': return 8
    case 'nine': return 9
    case 'ten': return 10
    default: return null
    }
}

const sortableTitle = (t: string) => {
    let title: string = t.toLocaleLowerCase()
    if (title.startsWith('the')) {
        title = title.substring(3).trim()
    }
    return title
}

const filterGames = (gameTypeSlug: string, games: JBGWeb.GameDetail[]): JBGWeb.GameDetail[] => {
    const filteredGames: JBGWeb.GameDetail[] = []

    // filter by duration if gametype includes 'minute'
    if (gameTypeSlug.includes('minute')) {
        let timeLimit = Number(gameTypeSlug.split('-')[0])
        if (Number.isNaN(timeLimit)) {
            // map string number to numeric value
            const t = mapStringToNumber(gameTypeSlug.split('-')[0])
            if (!t) {
                console.error('could not map string to numeric value: ', t)
                return []
            }
            timeLimit = Number(t)
        }
        games.forEach((game: JBGWeb.GameDetail) => {
            if (game.features.durationAvg <= timeLimit) {
                filteredGames.push(game)
            }
        })
    }

    // fitler by playerCount
    if (gameTypeSlug.includes('-player')) {
        let playerLimit = Number(gameTypeSlug.split('-')[0])
        if (Number.isNaN(playerLimit)) {
            const p = mapStringToNumber(gameTypeSlug.split('-')[0])
            if (!p) {
                console.error('could not map string to numeric value: ', p)
                return []
            }
            playerLimit = Number(p)
        }
        games.forEach((game: JBGWeb.GameDetail) => {
            if (game.features.counts.playerMin <= playerLimit && game.features.counts.playerMax >= playerLimit) {
                filteredGames.push(game)
            }
        })
    }

    // TODO: filter by persona when those exist in Strapi
    // TODO: filter by language when supported language exits in Stapi specs per game

    if (!gameTypeSlug.includes('minute') && !gameTypeSlug.includes('-player')) {
        // filter by game type tag
        games.forEach((game: JBGWeb.GameDetail) => {
            game.features.types.data.forEach((tagObj) => {
                if (tagObj?.attributes?.slug.toLowerCase() === gameTypeSlug) {
                    filteredGames.push(game)
                }
            })
        })
    }

    // alphabetize
    const alphabeticalFilteredGames = filteredGames.sort((a, b) => {
        const aTitle = sortableTitle(a.title)
        const bTitle = sortableTitle(b.title)
        return aTitle.localeCompare(bTitle)
    })
    return alphabeticalFilteredGames
}

interface pageData {
    content: JBGWeb.GameType,
    englishGames?: JBGWeb.GameDetail[] | null,
    games: JBGWeb.GameDetail[] | null
}

const app = useNuxtApp()
const i18n = useI18n()
const route = useRoute()
const gameTypeSlug = route.params.gametypeslug as string

const mapLangs = (languages: JBGWeb.Language[]): string[] => {
    const mappedLangs = []
    languages.forEach((lang) => {
        const s = String(lang.language)
        //  if not pt-br or es-xl; remove everything after and including _
        if (s.includes('XL') || s.includes('BR')) {
            // change underscores to hyphens; ex: pt_BR ---> pt-BR
            const l = s.replace('_', '-')
            mappedLangs.push(l)
        } else {
            mappedLangs.push(s.split('_')[0])
        }
    })
    return mappedLangs
}

const filterGamesToLocale = (games: JBGWeb.GameDetail[]): JBGWeb.GameDetail[] => {
    const localizedGames = []
    if (!games) return localizedGames
    const locale = i18n.locale.value
    games.forEach((g) => {
        const strapiLangs = g.features.languages
        // mapping langs from Strapi lang codes to i18n lang codes
        const langs = mapLangs(strapiLangs)
        const isLocalized = langs.some((lang) => lang.includes(locale))
        if (isLocalized) {
            localizedGames.push(g)
        }
    })
    return localizedGames
}

const { data: pageData } = await useAsyncData<pageData>(
    `gametype-${gameTypeSlug}-${i18n.locale.value}`,
    async () => {
        const data: pageData = {
            content: { name: '', slug: gameTypeSlug },
            games: []
        }
        try {
            // fetch text
            if (gameTypeSlug.includes('minute')) {
                data.content.name = gameTypeSlug
            } else if (gameTypeSlug.includes('-player')) {
                data.content.name = gameTypeSlug
            } else {
                const c: JBGWeb.GameType = await app.$cms.fetchOne('game-types', gameTypeSlug, {
                    // TODO: replace this locale field when we have localized game type content copy in Strapi
                    // locale: i18n.locale.value
                    locale: 'en'
                })
                data.content = c
            }

            const populate = ['tile', 'features.counts', 'features.types', 'packDetails', 'features.languages']

            // fetch games
            const g = await app.$cms.fetchMany<JBGWeb.GameDetail[]>('game-details', {
                populate,
                // TODO: replace this locale field when we have localized game type content copy in Strapi
                // locale: i18n.locale.value
                locale: 'en'
            })

            // filter and alphabetize the games
            data.games = filterGames(gameTypeSlug, g)

            // if non-english, do it all again in english
            if (i18n.locale.value !== 'en') {
                const enGames: JBGWeb.GameDetail[] = await app.$cms.fetchMany('game-details', {
                    populate,
                    locale: 'en'
                })
                data.englishGames = filterGames(gameTypeSlug, enGames)

                // do a second filter for non-english games down to the current locale using the languages field.
                // this won't be necessary once we have localized gametype content in Strapi.
                const localizedGames = filterGamesToLocale(data.games)
                data.games = localizedGames
            }
        } catch (error) {
            console.error(error)
            showError({
                statusCode: 404
            })
        }
        return data
    }
)

let title = ''
if (pageData.value.content.name) {
    title = `${pageData.value.content.name}`
}

useHead({
    title,
    meta: [
        {
            property: 'og:title',
            content: title
        }
    ]
})

const currentLanguage = computed(() => {
    switch (i18n.locale.value) {
    case 'fr':
        return 'Français'
    case 'it':
        return 'Italiano'
    case 'de':
        return 'Deutsch'
    case 'es':
        return 'Castellano'
    case 'es-XL':
    case 'es-419':
        return 'Español (América Latina)'
    case 'pt-br':
    case 'pt-BR':
        return 'Português brasileiro'
    default:
        return 'English'
    }
})

const reqImageWidth = computed(() => {
    if (import.meta.client) {
        if (window.innerWidth < 960) return 454
        if (window.innerWidth < 391) return 173
        return 305
    }
    return null
})

</script>

<style lang="scss" scoped>
@use "$styles/kit.scss" as *;

h1 {
    font-size: 48px;
    font-style: normal;
    font-weight: 800;
    line-height: 48px;

    @include mq-xsmall {
        font-size: 32px;
        line-height: 32px;
        margin-top: 16px;
    }
}

:deep(h2) {
    margin: -18px 0px 10px 0px;
    font-size: 20px;
    font-style: italic;
    font-weight: 700;
    line-height: 27px;
    text-align: center;

    @include mq-xsmall {
        font-size: 18px;
        margin: -10px 0px 8px 0px;
    }
}

.blurb {
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    text-align: center;

    @include mq-xsmall {
        font-size: 14px;
        line-height: 21px;
    }
}

.locale-heading {
    font-size: 20px;
    margin: 40px 0 0 8px;
    align-self: flex-start;

    @include mq-medium {
        font-size: 16px;
        margin-left:10px;
    }

    @include mq-small {
        font-size: 16px;
        margin-left: 14px;
    }

    @include mq-xsmall {
        font-size: 14px;
        margin-left: 18px;
    }
}

.category {
    align-items: center;
    // min-height: 100vh;

    .flex-column {
        width: calc(66%);
        justify-content: center;

        @include mq-small-and-less {
            padding: 0px 10px;
            width: 100%;
        }
    }

    .flex-row {
        // flip the direction for mobile
        @include mq-xsmall {
            flex-direction: column;
        }
    }

    .flex-row, .flex-column {
        align-items: center;
    }

    .category-icon {
        width: 45px;
        height: 36px;

        margin-right: 20px;

        @include mq-xsmall {
            margin-top: 16px;
            margin-right: 0px;
        }
    }

    .grid-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .category-grid {
        column-gap: 20px;
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        flex-wrap: wrap;
        padding-top: 20px;
        padding-bottom: 20px;
        row-gap: 32px;
        width: 100%;
        justify-content: center;
        max-width: 1200px;

        @include mq-small-and-less {
            grid-template-columns: repeat(3, 1fr);
            column-gap: 16px;
            row-gap: 24px;
        }

        @include mq-xsmall {
            grid-template-columns: repeat(2, 1fr);
            column-gap: 12px;
            row-gap: 16px;
        }

        &.centered {
            justify-content: center;
        }
    }

    .game-tile {
        // flex: 0 0 calc(25% - 1em);

        @include mq-small-and-less {
            // flex: 0 0 calc(50% - 1em);

            :deep(.game-img) {
                // width: 454px;
            }
        }
    }
}

</style>
